import React from 'react';

import { useRouter } from 'expo-router';
import {
  ButtonContainer,
  Container,
  StyledText,
  Title,
} from 'features/menu/components/menu-item-unavailable/styled';

import ActionButton from 'components/action-button';
import { getDayPartUnavailableMessage } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/get-day-part-unavailable-message';
import type { DayPartUnavailableKind } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/is-entry-out-of-active-day-parts';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { routes } from 'utils/routing';

type MenuDayPartUnavailableProps = {
  kind: DayPartUnavailableKind;
  dayParts: readonly string[];
};

/**
 * Daypart mismatch recovery: tell the guest when the item/category is available,
 * without pinning section chrome or Full Menu siblings.
 */
export function MenuDayPartUnavailable({ kind, dayParts }: MenuDayPartUnavailableProps) {
  const router = useRouter();
  const message = getDayPartUnavailableMessage(kind, dayParts);

  useLogUserErrorMessage({
    message,
    severity: LogUserErrorSeverity.Recoverable,
    context: {
      moreInfo: 'menu-day-part-unavailable.tsx',
      kind,
      dayParts: dayParts.join(','),
    },
  });

  return (
    <Container testID="menu-day-part-unavailable">
      <Title variant="headerOne" testID="menu-day-part-unavailable-title">
        {t('itemNotAvailable')}
      </Title>
      <StyledText variant="copyOne">{message}</StyledText>
      <ButtonContainer>
        <ActionButton
          testID="menu-day-part-unavailable-cta"
          fullWidth
          onPress={() => router.dismissTo(routes.menu)}
        >
          {t('backToMenu')}
        </ActionButton>
      </ButtonContainer>
    </Container>
  );
}
